import { API } from "@swivl-mono/api-helper";
import { GenericTableRowInterface } from "@swivl-mono/types";
import { useEffect, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import "./Analytics.Settings.View.scss";
import { CallToActionChart } from "./CallToActionChart";
import { DiscountChart } from "./DiscountsChart";
import { PayBillChart } from "./PayBillChart";
import { BotAnalyticsDashboard } from "./ReviewCharts";
import { ShoppingFunnelChart } from "./ShoppingFunnelChart";

export function AnalyticsPlaygroundView() {
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<GenericTableRowInterface[]>([]);
  const [tableId, setTableId] = useState("discounts-00");

  // initial date range = 30 days prior to today
  const formatDate = (date: Date) => date.toISOString().split("T")[0];
  const getTodayDate = () => formatDate(new Date());
  const getDate30DaysAgo = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return formatDate(date);
  };
  const [startDate, setStartDate] = useState(getDate30DaysAgo());
  const [endDate, setEndDate] = useState(getTodayDate());

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      try {
        const orgId = Member.orgId!;
        const constraints = {};
        const fetchedRows = await API.Tables.queryTable(orgId, tableId, startDate, endDate, constraints);
        setRows(fetchedRows);

        const columnConstraints = { isHeading: true };
        const columnNamesRow = await API.Tables.queryTable(1, tableId, "2024-12-01", getTodayDate(), columnConstraints);
        if (columnNamesRow.length > 0) {
          const columnNames = Object.values(columnNamesRow[0]);
          const updatedRows = fetchedRows.map((row) => {
            const updatedRow: GenericTableRowInterface = {
              ...row,
            };
            for (let i = 0; i < 75; i++) {
              const oldKey = `c${String(i + 1).padStart(2, "0")}`;
              const newKey = columnNames[i + 8];
              updatedRow[newKey] = row[oldKey];
              delete updatedRow[oldKey];
            }
            return updatedRow;
          });
          setRows(updatedRows);
        }
      } catch (error) {
        console.error("Error querying table: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRows();
  }, [startDate, endDate, tableId]);

  const renderChart = (tableId: string, rows: any[]) => {
    switch (tableId) {
      case "shoppingfunnel-00":
        return (
          <>
            <ShoppingFunnelChart data={rows} />
            <pre>{JSON.stringify(rows, null, 2)}</pre>
          </>
        );
      case "paybill-00":
        return (
          <>
            <PayBillChart data={rows} />
            <pre>{JSON.stringify(rows, null, 2)}</pre>
          </>
        );
      case "discounts-00":
        return (
          <>
            <DiscountChart data={rows} />
            <pre>{JSON.stringify(rows, null, 2)}</pre>
          </>
        );
      case "cta-00":
        return (
          <>
            <CallToActionChart data={rows} />
            <pre>{JSON.stringify(rows, null, 2)}</pre>
          </>
        );
      default:
        return <pre>{JSON.stringify(rows, null, 2)}</pre>;
    }
  };

  const showReviews = tableId === "reviews";
  return (
    <div className="p-5 flex flex-col">
      <h1>Analytics Playground</h1>
      <br></br>
      <label className="label">
        Start Date:
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
      </label>
      <label className="label">
        End Date:
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" />
      </label>
      <label className="label">
        Table of Interest:
        <select
          value={tableId}
          onChange={(e) => {
            setTableId(e.target.value);
          }}
        >
          <option value="fms-plus-abandoned-carts-00">Abandoned Cart Automated Sequences</option>
          <option value="account-balance-00">Account Balance</option>
          <option value="fms-plus-move-in-00">Move In Automated Sequences</option>
          <option value="sizedescriptions-00">Size Descriptions</option>
          <option value="sms-links-00">SMS Links</option>
          <option value="fms-plus-move-out-00">Move Out Automated Sequences</option>
          <option value="fms-plus-past-due-00">Past Due Automated Sequences</option>
          <option value="unitnumber-00">Unit Number</option>
          <option value="gatecode-00">Gate Code</option>
          <option value="profile-collect-00">Profile Collect</option>
          <option value="persona-collect-00">Persona Collect</option>
          <option value="discounts-00">Discounts</option>
          <option value="my-storage-00">My Storage</option>
          <option value="cta-00">Call to Action</option>
          <option value="feedback-00">Feedback</option>
          <option value="fms-00">Leads Sent into FMS</option>
          <option value="paybill-00">Pay Bill</option>
          <option value="shoppingfunnel-00">Shopping Funnel</option>
          <option value="getting-started-menu-00">Getting Started Menu</option>
          <option value="reviews">Reviews</option>
        </select>
      </label>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>{showReviews ? <BotAnalyticsDashboard orgId={Member.orgId!} startDay={startDate} endDay={endDate} /> : renderChart(tableId, rows)}</>
      )}
    </div>
  );
}
